import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import { usersApi } from '../../api/endpoints';
import { UserDto, UserRoleMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleChanges, setRoleChanges] = useState<Record<string, number>>({});
  const [savingRole, setSavingRole] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, meRes] = await Promise.all([
        usersApi.list(),
        usersApi.me(),
      ]);
      setUsers(usersRes.data);
      setCurrentUser(meRes.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('users.fetchError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRoleChange = useCallback(
    (userId: string, newRole: number) => {
      setRoleChanges((prev) => ({
        ...prev,
        [userId]: newRole,
      }));
    },
    []
  );

  const handleSaveRole = useCallback(
    async (userId: string) => {
      const newRole = roleChanges[userId];
      if (newRole === undefined) return;

      setSavingRole(userId);
      try {
        await usersApi.updateRole(userId, { role: newRole });
        toast.success(t('users.roleUpdated'));
        setRoleChanges((prev) => {
          const copy = { ...prev };
          delete copy[userId];
          return copy;
        });
        fetchData();
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('users.roleUpdateError'));
      } finally {
        setSavingRole(null);
      }
    },
    [roleChanges, fetchData]
  );

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const columns = [
    {
      key: 'displayName',
      header: t('users.name'),
      sortable: true,
    },
    {
      key: 'email',
      header: t('users.email'),
      sortable: true,
    },
    {
      key: 'role',
      header: t('users.role'),
      render: (row: UserDto) => {
        const currentRole = roleChanges[row.id] ?? row.role;
        const hasChange = roleChanges[row.id] !== undefined && roleChanges[row.id] !== row.role;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Select
              options={Object.entries(UserRoleMap).map(([key, label]) => ({ value: key, label }))}
              value={String(currentRole)}
              onChange={(value) => handleRoleChange(row.id, Number(value))}
              size="sm"
            />
            {hasChange && (
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleSaveRole(row.id)}
                disabled={savingRole === row.id}
              >
                {savingRole === row.id ? '...' : t('common.save')}
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: 'isActive',
      header: t('users.isActive'),
      render: (row: UserDto) =>
        row.isActive ? (
          <span className="badge badge-success">{t('common.active')}</span>
        ) : (
          <span className="badge badge-danger">{t('common.inactive')}</span>
        ),
    },
    {
      key: 'lastLoginAt',
      header: t('users.lastLogin'),
      sortable: true,
      render: (row: UserDto) => formatDate(row.lastLoginAt),
    },
  ];

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('users.title')}</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={fetchData}>
            <FiRefreshCw /> {t('common.refresh')}
          </button>
        </div>
      </div>

      {currentUser && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3>{t('users.loggedInUser')}</h3>
          </div>
          <div className="card-body">
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('users.name')}</span>
                <span className="detail-value">{currentUser.displayName}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('users.email')}</span>
                <span className="detail-value">{currentUser.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('users.role')}</span>
                <span className="detail-value">
                  {UserRoleMap[currentUser.role] || t('users.unknownRole')}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('common.status')}</span>
                <span className="detail-value">
                  {currentUser.isActive ? (
                    <span className="badge badge-success">{t('common.active')}</span>
                  ) : (
                    <span className="badge badge-danger">{t('common.inactive')}</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={users}
        totalCount={users.length}
        page={1}
        pageSize={users.length || 25}
        totalPages={1}
      />
    </div>
  );
};

export default UsersPage;
